import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, XCircle, Calendar, Clock, MapPin, Users, Edit3, Save, AlertTriangle, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicRegistrationManage — Page publique de gestion d'une inscription
 * (compétition / afterwork / portes ouvertes) via un manage_token reçu par email.
 * Permet à un invité de : consulter, modifier (notes, partner, guests) ou annuler
 * son inscription — sans compte portail client.
 */
export default function PublicRegistrationManage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | already_cancelled | error
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [form, setForm] = useState({ notes: '', partner_name: '', guests_count: 0 });

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/registrations/manage/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Lien invalide');
      }
      const d = await res.json();
      setData(d);
      setForm({
        notes: d.registration?.notes || '',
        partner_name: d.registration?.partner_name || '',
        guests_count: d.registration?.guests_count || 0,
        // 🎯 iter229 : couverts pour resto (défaut 1)
        covers: d.registration?.covers || 1,
      });
      if (d.registration?.status === 'cancelled') {
        setStatus('already_cancelled');
      } else {
        setStatus('ready');
      }
    } catch (e) {
      setStatus('error');
      setError(e.message || "Ce lien de gestion est invalide ou a expiré.");
    }
  };

  useEffect(() => { loadData(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { notes: form.notes };
      if (data?.kind === 'restaurant') {
        payload.covers = parseInt(form.covers || 1);
      } else {
        payload.partner_name = form.partner_name;
        if (data?.allow_guests) payload.guests_count = parseInt(form.guests_count || 0);
      }
      const res = await fetch(`${API_URL}/api/public/registrations/manage/${token}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || 'Erreur');
      setSuccessMsg('Vos modifications ont été enregistrées.');
      setEditing(false);
      await loadData();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/public/registrations/manage/${token}/cancel`, { method: 'POST' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || 'Erreur');
      setStatus('already_cancelled');
      setShowCancelConfirm(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return iso; }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
        <div className="text-center">
          <Loader2 size={40} className="mx-auto animate-spin text-emerald-600 mb-2" />
          <p className="text-neutral-600">Chargement de votre inscription…</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <XCircle size={20} /> Lien invalide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-neutral-600">{error || "Ce lien de gestion n&apos;est pas valide ou a expiré."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const reg = data?.registration || {};
  const isCancelled = status === 'already_cancelled';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-4">
      <div className="max-w-2xl mx-auto pt-8 pb-16">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium mb-3">
            <Sparkles size={12} /> Gestion de votre inscription
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-800">{data?.event_name}</h1>
          <p className="text-sm text-neutral-500 mt-1">
            {data?.event_type === 'afterwork' && '🍺 Afterwork'}
            {data?.event_type === 'competition' && '🏆 Compétition'}
            {data?.event_type === 'portes_ouvertes' && '🎾 Portes Ouvertes'}
            {data?.event_type === 'restaurant' && '🍽️ Réservation au restaurant'}
          </p>
        </div>

        {isCancelled && (
          <Card className="mb-4 border-red-200 bg-red-50" data-testid="reg-cancelled-card">
            <CardContent className="pt-6 text-center">
              <XCircle size={32} className="mx-auto text-red-500 mb-2" />
              <p className="font-medium text-red-800">Cette inscription est annulée.</p>
              <p className="text-xs text-red-600 mt-1">Aucune action supplémentaire n&apos;est possible.</p>
            </CardContent>
          </Card>
        )}

        {successMsg && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2" data-testid="reg-success-msg">
            <CheckCircle size={18} className="text-emerald-600" />
            <span className="text-sm text-emerald-800">{successMsg}</span>
          </div>
        )}
        {error && !editing && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        )}

        {/* Événement info */}
        <Card className="mb-4" data-testid="event-info-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Informations de l&apos;événement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-neutral-700">
              <Calendar size={16} className="text-neutral-400" />
              <span>{formatDate(data?.event_date)}</span>
            </div>
            {data?.event_time && (
              <div className="flex items-center gap-2 text-neutral-700">
                <Clock size={16} className="text-neutral-400" />
                <span>{data.event_time}</span>
              </div>
            )}
            {data?.event_location && (
              <div className="flex items-center gap-2 text-neutral-700">
                <MapPin size={16} className="text-neutral-400" />
                <span>{data.event_location}</span>
              </div>
            )}
            <div className="pt-2 mt-2 border-t flex items-center gap-2 flex-wrap">
              {data?.is_free ? (
                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">Événement gratuit</span>
              ) : (
                <>
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">Événement payant</span>
                  {reg.payment_status === 'paid' && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">✓ Paiement reçu</span>}
                  {(reg.payment_status === 'awaiting_payment' || reg.payment_status === 'pending' || !reg.payment_status) && (
                    <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-medium">Paiement à venir</span>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Inscription details + modification */}
        <Card className="mb-4" data-testid="reg-details-card">
          <CardHeader className="pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base">Votre {data?.kind === 'restaurant' ? 'réservation' : 'inscription'}</CardTitle>
            {!isCancelled && !editing && (data?.kind === 'competition' || data?.kind === 'restaurant') && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="reg-edit-btn">
                <Edit3 size={14} className="mr-1" /> Modifier
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs text-neutral-500 uppercase">Nom</Label>
              <p className="text-sm font-medium">{reg.client_name || '—'}</p>
            </div>
            <div>
              <Label className="text-xs text-neutral-500 uppercase">Email</Label>
              <p className="text-sm">{reg.client_email || '—'}</p>
            </div>

            {editing ? (
              <>
                {data?.kind === 'restaurant' ? (
                  <div>
                    <Label className="text-xs text-neutral-500 uppercase" htmlFor="covers">Nombre de couverts</Label>
                    <Input
                      id="covers"
                      type="number"
                      min="1"
                      max="20"
                      data-testid="reg-covers-input"
                      value={form.covers}
                      onChange={(e) => setForm({ ...form, covers: e.target.value })}
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs text-neutral-500 uppercase" htmlFor="partner_name">Partenaire (pour compétition en double)</Label>
                      <Input
                        id="partner_name"
                        data-testid="reg-partner-input"
                        value={form.partner_name}
                        onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                        placeholder="Prénom Nom de votre partenaire"
                        maxLength={120}
                      />
                    </div>
                    {data?.allow_guests && (
                      <div>
                        <Label className="text-xs text-neutral-500 uppercase" htmlFor="guests_count">
                          Accompagnants (afterwork gratuit)
                        </Label>
                        <Input
                          id="guests_count"
                          type="number"
                          min="0"
                          max="20"
                          data-testid="reg-guests-input"
                          value={form.guests_count}
                          onChange={(e) => setForm({ ...form, guests_count: e.target.value })}
                        />
                      </div>
                    )}
                  </>
                )}
                <div>
                  <Label className="text-xs text-neutral-500 uppercase" htmlFor="notes">
                    {data?.kind === 'restaurant' ? 'Remarque / allergies (optionnel)' : 'Remarque (optionnel)'}
                  </Label>
                  <Textarea
                    id="notes"
                    data-testid="reg-notes-input"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Allergies, remarques, informations utiles…"
                    maxLength={500}
                    rows={3}
                  />
                </div>
                {error && (
                  <div className="p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700">{error}</div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="reg-save-btn">
                    {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Enregistrement…</> : <><Save size={14} className="mr-1" /> Enregistrer</>}
                  </Button>
                  <Button variant="outline" onClick={() => { setEditing(false); setError(''); setForm({ notes: reg.notes || '', partner_name: reg.partner_name || '', guests_count: reg.guests_count || 0, covers: reg.covers || 1 }); }} disabled={saving}>
                    Annuler
                  </Button>
                </div>
              </>
            ) : (
              <>
                {reg.partner_name && (
                  <div>
                    <Label className="text-xs text-neutral-500 uppercase">Partenaire</Label>
                    <p className="text-sm">{reg.partner_name}</p>
                  </div>
                )}
                {data?.kind === 'restaurant' && reg.covers > 0 && (
                  <div>
                    <Label className="text-xs text-neutral-500 uppercase flex items-center gap-1"><Users size={12} /> Couverts</Label>
                    <p className="text-sm">{reg.covers} personne{reg.covers > 1 ? 's' : ''}</p>
                  </div>
                )}
                {data?.allow_guests && reg.guests_count > 0 && (
                  <div>
                    <Label className="text-xs text-neutral-500 uppercase flex items-center gap-1"><Users size={12} /> Accompagnants</Label>
                    <p className="text-sm">{reg.guests_count} personne{reg.guests_count > 1 ? 's' : ''}</p>
                  </div>
                )}
                {reg.notes && (
                  <div>
                    <Label className="text-xs text-neutral-500 uppercase">Remarque</Label>
                    <p className="text-sm italic text-neutral-600">&laquo;&nbsp;{reg.notes}&nbsp;&raquo;</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Annuler */}
        {!isCancelled && !editing && (
          <Card className="border-red-200" data-testid="reg-cancel-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-red-700">
                Annuler ma {data?.kind === 'restaurant' ? 'réservation' : 'inscription'}
              </CardTitle>
              <CardDescription className="text-xs">Cette action est définitive et libèrera votre place.</CardDescription>
            </CardHeader>
            <CardContent>
              {!showCancelConfirm ? (
                <Button variant="outline" onClick={() => setShowCancelConfirm(true)} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="reg-cancel-btn">
                  <XCircle size={14} className="mr-1" /> Annuler ma {data?.kind === 'restaurant' ? 'réservation' : 'inscription'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 rounded bg-amber-50 border border-amber-200 flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Confirmez-vous l&apos;annulation de votre {data?.kind === 'restaurant' ? 'réservation' : 'inscription'}
                      {data?.event_name ? <> à <strong>{data.event_name}</strong></> : null} ?
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="destructive" onClick={handleCancel} disabled={cancelling} className="flex-1" data-testid="reg-cancel-confirm">
                      {cancelling ? <><Loader2 size={14} className="mr-1 animate-spin" /> Annulation…</> : 'Oui, annuler'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowCancelConfirm(false)} disabled={cancelling}>
                      Non
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
